/**
 * ThemePicker — palette swatch button in the top-right of the tape bar.
 * Clicking opens a small popover with six preset themes. Pick one and the
 * CSS variables on <html> swap immediately; choice persists in localStorage.
 *
 * Color-blind notes:
 *   - "Color-Blind Safe" uses Wong's 2011 palette (#0072B2 / #E69F00) which
 *     is distinguishable for deuteranopia, protanopia, AND tritanopia —
 *     no red/green axis at all, only blue and amber.
 *   - "High Contrast" goes monochrome and pushes direction onto ▲ / ▼
 *     glyphs rendered via CSS pseudo-elements (added in theme.css).
 */
import { useState, useRef, useEffect } from 'react';
import { useStore, THEME_PRESETS } from '@/store';
import { FONT, MUT } from '@/theme';

export function ThemePicker() {
  const current = useStore(s => s.theme);
  const setTheme = useStore(s => s.setTheme);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside-to-close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const currentSwatch = THEME_PRESETS.find(p => p.id === current)?.swatch ?? '#ff9900';

  return (
    <div ref={ref} style={{ position: 'relative', pointerEvents: 'auto' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Theme"
        style={{
          width: 22, height: 22, borderRadius: 11,
          background: currentSwatch,
          border: '1px solid #000',
          boxShadow: '0 0 0 1px ' + currentSwatch + '55',
          cursor: 'pointer', padding: 0,
        }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '110%', right: 0, zIndex: 50,
          background: '#0e0e0e', border: '1px solid #222', borderRadius: 4,
          padding: 8, minWidth: 220,
          fontFamily: FONT, fontSize: 10,
          boxShadow: '0 6px 20px rgba(0,0,0,0.6)',
        }}>
          <div style={{ color: 'var(--accent)', fontSize: 9, letterSpacing: 2, marginBottom: 6 }}>
            THEME
          </div>
          {THEME_PRESETS.map(p => {
            const active = p.id === current;
            return (
              <button
                key={p.id}
                onClick={() => { setTheme(p.id); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '5px 6px', marginBottom: 2,
                  background: active ? '#1a1a1a' : 'transparent',
                  border: '1px solid ' + (active ? 'var(--accent)' : 'transparent'),
                  color: '#e0e0e0', cursor: 'pointer',
                  fontFamily: FONT, fontSize: 11, textAlign: 'left',
                  borderRadius: 2,
                }}
              >
                <span style={{
                  width: 14, height: 14, borderRadius: 7,
                  background: p.swatch, flexShrink: 0,
                  border: '1px solid #222',
                }} />
                <span style={{ flex: 1 }}>{p.label}</span>
                {active && <span style={{ color: 'var(--accent)', fontSize: 10 }}>✓</span>}
              </button>
            );
          })}
          {THEME_PRESETS.find(p => p.id === current)?.note && (
            <div style={{ color: MUT, fontSize: 9, marginTop: 6, lineHeight: 1.4 }}>
              {THEME_PRESETS.find(p => p.id === current)!.note}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
